import React from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  Mail, 
  Globe, 
  Palette, 
  FileText, 
  Settings,
  CheckCircle2 
} from 'lucide-react';
import { AdminTenantFormData, INDUSTRY_OPTIONS, SPECIALISATION_OPTIONS } from './types';

interface Props {
  data: AdminTenantFormData;
  updateData: (updates: Partial<AdminTenantFormData>) => void;
}

const StepReview: React.FC<Props> = ({ data, updateData }) => {
  const industryLabel = INDUSTRY_OPTIONS.find((i) => i.value === data.industry)?.label || data.industry;
  
  const totalFiles = 
    (data.logoFile ? 1 : 0) + 
    (data.brandGuidelinesFile ? 1 : 0) + 
    data.fontFiles.length + 
    data.documentFiles.length;

  return (
    <div className="space-y-6">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-primary">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-medium">Ready to provision</span>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Review the details below, then click Create Tenant to provision this client.
        </p>
      </div>

      {/* Client Details */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Building2 className="h-4 w-4" />
          Client Details
        </div>
        <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Business</span>
            <span className="font-medium">{data.businessName || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Contact</span>
            <span>{data.contactName || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email</span>
            <span>{data.email || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Industry</span>
            <span className="capitalize">{industryLabel}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Package</span>
            <Badge variant="secondary" className="capitalize">
              {data.packageType} (complimentary)
            </Badge>
          </div>
        </div>
      </div>

      {/* Website & Developer */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Globe className="h-4 w-4" />
          Website & Developer
        </div>
        <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Website</span>
            <span className="truncate max-w-[200px]">{data.websiteUrl || '—'}</span>
          </div>
          {data.additionalUrls.filter(Boolean).length > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Additional URLs</span>
              <span>{data.additionalUrls.filter(Boolean).length}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Developer Email</span>
            <span>{data.developerEmail || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Install Scope</span>
            <span className="capitalize">{data.installScope.replace('-', ' ')}</span>
          </div>
        </div>
      </div>

      {/* Brand & Assets */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Palette className="h-4 w-4" />
          Brand & Assets
        </div>
        <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Design Reference</span>
            <span>
              {data.useWebsiteAsDesignReference 
                ? 'Website Design' 
                : `${totalFiles} file${totalFiles !== 1 ? 's' : ''} uploaded`}
            </span>
          </div>
          {!data.useWebsiteAsDesignReference && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Colors</span>
              <div className="flex gap-1">
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: data.brandColors.primary }}
                  title="Primary"
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: data.brandColors.secondary }}
                  title="Secondary"
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: data.brandColors.accent }}
                  title="Accent"
                />
              </div>
            </div>
          )}
          {data.useWebsiteAsDesignReference && totalFiles > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Override Files</span>
              <span>{totalFiles} file{totalFiles !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>

      {/* Bot Config */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Settings className="h-4 w-4" />
          Bot Configuration
        </div>
        <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Specialisations</span>
            <span>{data.specialisations.length} selected</span>
          </div>
          {data.specialisations.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {data.specialisations.slice(0, 4).map((s) => {
                const label = SPECIALISATION_OPTIONS.find((o) => o.value === s)?.label || s;
                return (
                  <Badge key={s} variant="outline" className="text-xs">
                    {label}
                  </Badge>
                );
              })}
              {data.specialisations.length > 4 && (
                <Badge variant="outline" className="text-xs">
                  +{data.specialisations.length - 4} more
                </Badge>
              )}
            </div>
          )}
          {data.doNotMention && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Topics to avoid</span>
              <span>Configured</span>
            </div>
          )}
        </div>
      </div>

      {/* Notification Option */}
      <div className="border-t pt-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={data.sendClientNotification}
            onCheckedChange={(checked) =>
              updateData({ sendClientNotification: checked === true })
            }
          />
          <div>
            <span className="text-sm font-medium">Send welcome email to client</span>
            <p className="text-xs text-muted-foreground">
              Client will receive an info email (no login required until assistant is ready)
            </p>
          </div>
        </label>
      </div>
    </div>
  );
};

export default StepReview;
